import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@eatclubapp/ec-component-library';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getRestaurantConfig } from '../../../constants/appConfig';
import {
  getHolidayDateBoundsForProfile,
  getHolidayHoursProfileForRestaurant,
} from '../../../utils/holidayHoursProfile';
import { isVenueInactiveForPromoModals } from '../../../utils/helpers';
import { PrimaryButton } from '../../button/PrimaryButton';
import { Modal } from '../../Modal';
import Mixpanel from '../../../lib/mixpanel';

const HolidayHoursModal = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const breakpointSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const restaurant = useSelector((state) => state.restaurantActive.restaurant);
  const holidayProfile = getHolidayHoursProfileForRestaurant(
    restaurant,
    getRestaurantConfig(restaurant).holidayHours,
  );

  const holidayDateBounds = useMemo(
    () => getHolidayDateBoundsForProfile(holidayProfile),
    [holidayProfile],
  );

  const holidaySummaries = holidayProfile?.holidaySummaries ?? [];
  const plural = holidaySummaries.length > 1;
  const modalIsOpen = isOpen && !isVenueInactiveForPromoModals(restaurant);

  const modalHeading = plural
    ? 'Let’s get ready for upcoming public holidays.'
    : 'Let’s get ready for the upcoming public holiday.';

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (modalIsOpen && !wasOpenRef.current) {
      Mixpanel.track('HDS entry modal shown', {
        origin: window.location.origin,
        holiday_start: holidayDateBounds?.startDate,
        holiday_end: holidayDateBounds?.endDate,
        hds_modal_start: holidayProfile?.hdsModalStartDate,
        hds_modal_end: holidayProfile?.hdsModalEndDate,
        holiday_names: holidaySummaries.map((s) => s.displayName),
      });
    }
    wasOpenRef.current = modalIsOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit holidaySummaries; Mixpanel fires once when opening
  }, [
    modalIsOpen,
    holidayDateBounds?.endDate,
    holidayDateBounds?.startDate,
    holidayProfile?.hdsModalEndDate,
    holidayProfile?.hdsModalStartDate,
  ]);

  return (
    <Modal
      heading={modalHeading}
      isLoading={false}
      isOpen={modalIsOpen}
      showCloseButton={false}
      hideFooter
      onConfirm={() => {
        onClose();
      }}
      inputStyle={{
        button: { backgroundColor: '#FAD14C', color: theme.colors.charcoalTransparent },
      }}
    >
      <Box>
        {plural
          ? 'Please take a moment to update your opening hours and offers ahead of these public holidays.'
          : 'Please take a moment to update your opening hours and offers ahead of the public holiday.'}
      </Box>

      {holidaySummaries.length > 0 ? (
        <Box
          component='ul'
          style={{
            marginTop: '16px',
            marginBottom: 0,
            paddingLeft: '20px',
          }}
        >
          {holidaySummaries.map((s) => (
            <li key={s.id}>{s.displayName}</li>
          ))}
        </Box>
      ) : null}

      <Box
        style={{
          display: 'flex',
          width: '100%',
          gap: '20px',
          justifyContent: 'flex-end',
          paddingTop: '40px',
        }}
      >
        <PrimaryButton
          onClick={() => {
            Mixpanel.track('HDS entry modal review holiday hours', {
              origin: window.location.origin,
              holiday_start: holidayDateBounds?.startDate,
              holiday_end: holidayDateBounds?.endDate,
              hds_modal_start: holidayProfile?.hdsModalStartDate,
              hds_modal_end: holidayProfile?.hdsModalEndDate,
              holiday_names: holidaySummaries.map((s) => s.displayName),
            });
            history.push({
              pathname: '/profile/holiday-hours',
              search: holidayDateBounds
                ? `?startDate=${holidayDateBounds.startDate}&endDate=${holidayDateBounds.endDate}`
                : '',
            });
            onClose();
          }}
          fullWidth={breakpointSmDown}
          color='secondary'
          variant='contained'
          size='large'
          style={{ button: { height: '44px', fontWeight: '500' } }}
        >
          Review holiday hours
        </PrimaryButton>
      </Box>
    </Modal>
  );
};

HolidayHoursModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default HolidayHoursModal;
